<template>
  <div class="menu-preview">
    <b-alert v-if="!menuId" show variant="info" class="mb-0">
      Selecciona un menú para verlo.
    </b-alert>

    <div v-else>
      <div v-if="loading" class="text-center py-5">
        <b-spinner></b-spinner>
        <p class="text-muted mt-2 mb-0">Cargando el menú…</p>
      </div>

      <template v-else-if="unified">
        <!-- Barra de control -->
        <b-row align-v="center" class="mb-3">
          <b-col md="7">
            <div class="d-flex flex-wrap">
              <div v-for="s in resumen" :key="s.label" class="mr-4 mb-2">
                <div class="h4 mb-0">{{ s.valor }}</div>
                <small class="text-muted">{{ s.label }}</small>
              </div>
            </div>
          </b-col>
          <b-col md="5" class="text-md-right">
            <b-form-checkbox v-model="soloProblemas" switch class="d-inline-block">
              Solo con problemas
            </b-form-checkbox>
          </b-col>
        </b-row>

        <!-- Avisos: lo que no llegaría al cliente -->
        <b-alert v-if="sinPos.length" show variant="danger" class="py-2">
          <strong>{{ sinPos.length }}</strong> producto(s) sin producto del POS ligado.
          No se pueden convertir en línea de ticket, así que la app los oculta.
        </b-alert>
        <b-alert v-if="huerfanos.length" show variant="warning" class="py-2">
          <strong>{{ huerfanos.length }}</strong> producto(s) fuera de toda categoría:
          existen en el menú pero nadie los vería.
          <span class="text-muted">{{ huerfanos.map(i => i.name).join(', ') }}</span>
        </b-alert>
        <b-alert v-if="gruposSinUso.length" show variant="warning" class="py-2">
          <strong>{{ gruposSinUso.length }}</strong> grupo(s) de modificadores que ningún producto ofrece:
          <span class="text-muted">{{ gruposSinUso.map(g => g.name).join(', ') }}</span>
        </b-alert>

        <!-- El menú -->
        <section v-for="cat in categoriasVisibles" :key="cat.id" class="mb-5">
          <h3 class="menu-preview__cat">
            {{ cat.name }}
            <b-badge v-if="!cat.is_active" variant="secondary" class="ml-2 align-middle">inactiva</b-badge>
          </h3>

          <div class="menu-preview__grid">
            <article
              v-for="it in itemsVisiblesDe(cat.id)"
              :key="cat.id + it.id"
              class="menu-preview__card"
              :class="{ 'menu-preview__card--off': !it.is_active }">
              <div class="menu-preview__body">
                <h4 class="menu-preview__name">{{ it.name }}</h4>

                <div class="menu-preview__price">
                  <span>{{ dinero(precioFinal(it)) }}</span>
                  <span v-if="tienePromo(it)" class="menu-preview__price-old">{{ dinero(it.price) }}</span>
                </div>

                <p v-if="it.description" class="menu-preview__desc">{{ it.description }}</p>

                <div class="menu-preview__tags">
                  <span v-if="tienePromo(it)" class="menu-preview__badge menu-preview__badge--promo">
                    {{ it.promo_text || descuento(it) }}
                  </span>
                  <span v-if="gruposDe(it.id).length" class="menu-preview__badge menu-preview__badge--opts">
                    {{ gruposDe(it.id).length }} grupo(s) de opciones
                  </span>
                  <span v-if="!it.is_active" class="menu-preview__badge">inactivo</span>
                  <span v-if="!it.external_id" class="menu-preview__badge menu-preview__badge--bad">sin POS</span>
                  <span v-if="esPersonalizacion(it)" class="menu-preview__badge menu-preview__badge--info">
                    personalización
                  </span>
                </div>
              </div>

              <div class="menu-preview__thumb">
                <img v-if="it.image_url" :src="imageSrc(it.image_url)" :alt="it.name" />
                <div v-else class="menu-preview__thumb-empty">sin imagen</div>
              </div>

              <!-- Abre el detalle del producto, como el + del menú real -->
              <button
                v-if="gruposDe(it.id).length"
                type="button"
                class="menu-preview__add"
                :title="'Ver opciones de ' + it.name"
                @click="abrir(it)">+</button>
            </article>
          </div>

          <p v-if="!itemsVisiblesDe(cat.id).length" class="text-muted">
            <small v-if="soloProblemas">Sin productos con problemas en esta categoría.</small>
            <small v-else>Categoría vacía.</small>
          </p>
        </section>

        <b-alert v-if="!categoriasVisibles.length" show variant="secondary" class="mb-0">
          <span v-if="soloProblemas">No hay nada con problemas en este menú.</span>
          <span v-else>Este menú todavía no tiene categorías.</span>
        </b-alert>
      </template>
    </div>

    <!-- Detalle del producto: lo que vería el cliente al tocarlo -->
    <b-modal v-model="mostrarDetalle" :title="detalle ? detalle.name : ''" ok-only ok-title="Cerrar" size="lg">
      <template v-if="detalle">
        <div class="d-flex mb-3">
          <div class="flex-grow-1 pr-3">
            <div class="menu-preview__price mb-1">
              <span>{{ dinero(precioFinal(detalle)) }}</span>
              <span v-if="tienePromo(detalle)" class="menu-preview__price-old">{{ dinero(detalle.price) }}</span>
            </div>
            <p class="text-muted mb-1">{{ detalle.description || 'Sin descripción.' }}</p>
            <small class="text-muted">
              <code>{{ detalle.sku || '—' }}</code>
              <span v-if="detalle.external_id"> · {{ refEtiqueta(detalle) }} {{ detalle.external_id }}</span>
            </small>
          </div>
          <img v-if="detalle.image_url" :src="imageSrc(detalle.image_url)" class="menu-preview__thumb-lg" :alt="detalle.name" />
        </div>

        <div v-for="g in gruposDe(detalle.id)" :key="g.id" class="mb-3">
          <div>
            <strong>{{ g.name }}</strong>
            <small class="text-muted ml-2">elige {{ g.required_min }}–{{ g.required_max }}</small>
            <b-badge v-if="g.required_min > 0" variant="warning" class="ml-2">obligatorio</b-badge>
          </div>
          <div v-for="o in opcionesDe(g.id)" :key="g.id + o.item_id" class="d-flex justify-content-between border-bottom py-1">
            <span>
              {{ nombreItem(o.item_id) }}
              <b-badge v-if="esPersonalizacionPorId(o.item_id)" variant="info" class="ml-1">personalización</b-badge>
              <b-badge v-if="!externalIdDe(o.item_id)" variant="danger" class="ml-1">sin POS</b-badge>
            </span>
            <span class="text-muted">
              <template v-if="precioOpcion(o) > 0">+{{ dinero(precioOpcion(o)) }}</template>
              <template v-else>
                incluido
                <b-badge v-if="adicionalSospechoso(o)" variant="warning" class="ml-1"
                  :title="'El producto cuesta ' + dinero(itemPorId[o.item_id].price) + ' por separado'">
                  ¿sin precio?
                </b-badge>
              </template>
            </span>
          </div>
          <div v-if="!opcionesDe(g.id).length" class="text-danger">
            <small>Sin opciones — el grupo no le sirve de nada al cliente.</small>
          </div>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import axios from "axios";
import svc from "../../services/platformMenuService";

// Vista de solo lectura del menú ya armado, con el formato en que lo ve el
// cliente: categorías con sus productos en tarjeta. Encima marca lo que haría
// que algo no llegara a la app. No escribe nada.
export default {
  name: "PlatformMenuPreview",
  props: {
    menuId: { type: [String, Number], default: "" }
  },
  data() {
    return {
      loading: false,
      unified: null,
      soloProblemas: false,
      detalle: null,
      mostrarDetalle: false
    };
  },
  computed: {
    items() {
      return (this.unified && this.unified.items) || [];
    },
    itemPorId() {
      const m = {};
      this.items.forEach(i => { m[i.id] = i; });
      return m;
    },
    // Los MODIFIER viven dentro de los grupos, no como tarjeta de categoría.
    esModificador() {
      const m = {};
      this.items.forEach(i => { m[i.id] = String(i.type || "").toUpperCase() === "MODIFIER"; });
      return m;
    },
    categorias() {
      return ((this.unified && this.unified.categories) || [])
        .slice()
        .sort((a, b) => (a.sorting_position || 0) - (b.sorting_position || 0));
    },
    categoriasVisibles() {
      if (!this.soloProblemas) return this.categorias;
      return this.categorias.filter(c => this.itemsVisiblesDe(c.id).length);
    },
    grupos() {
      return (this.unified && this.unified.modifier_groups) || [];
    },
    grupoPorId() {
      const m = {};
      this.grupos.forEach(g => { m[g.id] = g; });
      return m;
    },
    // Productos que no cuelgan de ninguna categoría: existen pero nadie los ve.
    huerfanos() {
      const enCategoria = new Set(
        ((this.unified && this.unified.category_items) || []).map(ci => ci.item_id)
      );
      return this.items.filter(i => !this.esModificador[i.id] && !enCategoria.has(i.id));
    },
    gruposSinUso() {
      const usados = new Set(
        ((this.unified && this.unified.item_modifier_groups) || []).map(l => l.modifier_group_id)
      );
      return this.grupos.filter(g => !usados.has(g.id));
    },
    sinPos() {
      return this.items.filter(i => !i.external_id);
    },
    resumen() {
      const productos = this.items.filter(i => !this.esModificador[i.id]).length;
      return [
        { label: "categorías", valor: this.categorias.length },
        { label: "productos", valor: productos },
        { label: "modificadores", valor: this.items.length - productos },
        { label: "grupos", valor: this.grupos.length },
        { label: "sin POS", valor: this.sinPos.length }
      ];
    }
  },
  watch: {
    menuId: {
      immediate: true,
      handler(val) {
        if (val) this.cargar();
        else this.unified = null;
      }
    }
  },
  methods: {
    cargar() {
      this.loading = true;
      svc.unified(this.menuId)
        .then(r => { this.unified = r.data || null; })
        .catch(err => {
          this.unified = null;
          this.$bvToast.toast(
            err.response?.data?.message || "No se pudo cargar el menú",
            { title: "Error", variant: "danger", autoHideDelay: 5000 }
          );
        })
        .finally(() => { this.loading = false; });
    },
    abrir(it) {
      this.detalle = it;
      this.mostrarDetalle = true;
    },
    itemsDe(categoryId) {
      return ((this.unified && this.unified.category_items) || [])
        .filter(ci => ci.category_id === categoryId)
        .slice()
        .sort((a, b) => (a.sorting_position || 0) - (b.sorting_position || 0))
        .map(ci => this.itemPorId[ci.item_id])
        .filter(i => i && !this.esModificador[i.id]);
    },
    itemsVisiblesDe(categoryId) {
      const items = this.itemsDe(categoryId);
      if (!this.soloProblemas) return items;
      return items.filter(i => this.tieneProblema(i));
    },
    // Un producto "tiene problema" si no llegaría bien al cliente.
    tieneProblema(it) {
      if (!it.external_id || !it.is_active) return true;
      return this.gruposDe(it.id).some(
        g => !this.opcionesDe(g.id).length ||
             this.opcionesDe(g.id).some(o => !this.externalIdDe(o.item_id))
      );
    },
    gruposDe(itemId) {
      return ((this.unified && this.unified.item_modifier_groups) || [])
        .filter(l => l.item_id === itemId)
        .map(l => this.grupoPorId[l.modifier_group_id])
        .filter(Boolean);
    },
    opcionesDe(groupId) {
      return ((this.unified && this.unified.modifier_items) || [])
        .filter(o => o.modifier_group_id === groupId)
        .slice()
        .sort((a, b) => (a.sorting_position || 0) - (b.sorting_position || 0));
    },
    // El precio del grupo es el PRECIO ADICIONAL y manda tal cual: 0 significa
    // que la opción va incluida. Antes se caía al precio del item, y eso hacía
    // que un combo que anuncia "1 bebida a tu elección" cobrara la bebida.
    precioOpcion(o) {
      return Number(o.price) || 0;
    },
    // Adicional en 0 sobre un producto que sí cuesta: puede ser correcto
    // (va incluido) o ser un precio que se olvidó capturar. Se marca para que
    // alguien lo mire, en vez de cobrarlo por si acaso.
    adicionalSospechoso(o) {
      const it = this.itemPorId[o.item_id];
      return !Number(o.price) && !!it && Number(it.price) > 0;
    },
    // Hay promoción solo si el precio promocional es menor al de lista;
    // uno mayor o igual no es promoción y mostrarlo tachado confundiría.
    tienePromo(it) {
      const p = Number(it.promo_price);
      return !!p && p > 0 && p < Number(it.price);
    },
    precioFinal(it) {
      return this.tienePromo(it) ? Number(it.promo_price) : Number(it.price) || 0;
    },
    descuento(it) {
      const base = Number(it.price);
      if (!base) return "";
      return Math.round((1 - Number(it.promo_price) / base) * 100) + "% off";
    },
    nombreItem(itemId) {
      const it = this.itemPorId[itemId];
      return it ? it.name : itemId;
    },
    externalIdDe(itemId) {
      const it = this.itemPorId[itemId];
      return it ? it.external_id : 0;
    },
    esPersonalizacion(it) {
      return it.ref_type === "personalization";
    },
    esPersonalizacionPorId(itemId) {
      const it = this.itemPorId[itemId];
      return !!it && it.ref_type === "personalization";
    },
    refEtiqueta(it) {
      return this.esPersonalizacion(it) ? "personalización" : "producto";
    },
    dinero(n) {
      return "MX$" + Number(n || 0).toFixed(2);
    },
    imageSrc(url) {
      if (!url) return "";
      if (/^https?:\/\//.test(url)) return url;
      const base = (axios.defaults.baseURL || "").replace(/\/$/, "");
      return base + "/" + url.replace(/^\//, "");
    }
  }
};
</script>

<style scoped>
.menu-preview__cat {
  font-weight: 700;
  margin-bottom: 1rem;
}

/* Dos columnas como el menú real; una sola en pantallas angostas. */
.menu-preview__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 1rem;
}

.menu-preview__card {
  position: relative;
  display: flex;
  border: 1px solid #e6e6e6;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  min-height: 150px;
}

.menu-preview__card--off {
  opacity: 0.55;
}

.menu-preview__body {
  flex: 1 1 auto;
  padding: 1rem;
  padding-bottom: 2.5rem;
  min-width: 0;
}

.menu-preview__name {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.menu-preview__price {
  font-size: 0.95rem;
}

.menu-preview__price-old {
  color: #b00020;
  text-decoration: line-through;
  margin-left: 0.4rem;
}

/* Recorte a dos líneas, como en el menú real: la ficha completa vive en el
   detalle, no en la tarjeta. */
.menu-preview__desc {
  color: #6c757d;
  font-size: 0.85rem;
  margin: 0.4rem 0 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.menu-preview__tags {
  margin-top: 0.5rem;
}

.menu-preview__badge {
  display: inline-block;
  font-size: 0.7rem;
  border-radius: 4px;
  padding: 0.1rem 0.4rem;
  margin: 0 0.25rem 0.25rem 0;
  background: #eceff1;
  color: #37474f;
}

.menu-preview__badge--promo { background: #d32f2f; color: #fff; }
.menu-preview__badge--bad   { background: #d32f2f; color: #fff; }
.menu-preview__badge--info  { background: #0288d1; color: #fff; }
.menu-preview__badge--opts  { background: #e8f5e9; color: #2e7d32; }

.menu-preview__thumb {
  flex: 0 0 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
}

.menu-preview__thumb img {
  width: 140px;
  height: 100%;
  object-fit: cover;
}

.menu-preview__thumb-empty {
  color: #b0bec5;
  font-size: 0.75rem;
}

.menu-preview__thumb-lg {
  width: 160px;
  height: 160px;
  object-fit: cover;
  border-radius: 8px;
}

.menu-preview__add {
  position: absolute;
  right: 0.6rem;
  bottom: 0.6rem;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid #d0d0d0;
  background: #fff;
  color: #333;
  font-size: 1.1rem;
  line-height: 1;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.menu-preview__add:hover {
  background: #f5f5f5;
}
</style>
